import { useRef, useState } from "react";
import AddWalletIcon from "@/assets/add-wallet.svg?react";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  FULL_WIDTH_TALL_BUTTON,
  GREEN_ACTION_BUTTON_DISABLED,
  GREEN_ACTION_BUTTON_ENABLED,
} from "@/lib/buttonStyles";

const KENNITALA_LENGTH = 10;
const CORPORATE_ERROR_MESSAGE = "Kennitala fyrirtækis of stutt";

const isInvalidKennitala = (value: string) =>
  value.length > 0 && value.length < KENNITALA_LENGTH;

interface Props {
  bank: string;
  setBank: (bank: string) => void;
  hb: string;
  setHb: (hb: string) => void;
  reikn: string;
  setReikn: (reikn: string) => void;
  corporateId: string;
  setCorporateId: (corporateId: string) => void;
  addAccount: () => void;
  isCorporate: boolean;
  /** When true, render without Card wrapper (e.g. inside a modal) */
  inModal?: boolean;
}

export default function AddAccount({
  bank,
  setBank,
  hb,
  setHb,
  reikn,
  setReikn,
  corporateId,
  setCorporateId,
  addAccount,
  isCorporate,
  inModal = false,
}: Props) {
  const reiknRef = useRef<HTMLInputElement>(null);
  const hbRef = useRef<HTMLInputElement>(null);
  const [corporateIdBlurredWithError, setCorporateIdBlurredWithError] =
    useState(false);
  const [hbBlurredWithWarning, setHbBlurredWithWarning] = useState(false);

  const corporateIdError =
    isCorporate &&
      corporateIdBlurredWithError &&
      isInvalidKennitala(corporateId)
      ? CORPORATE_ERROR_MESSAGE
      : null;
  const isFormInvalid = !!corporateIdError;
  const isHbWarning = hbBlurredWithWarning && hb.length > 0 && hb !== "26";

  const handleCorporateKennitalaBlur = (
    e: React.FocusEvent<HTMLInputElement>,
  ) => {
    const cleaned = e.target.value.replace(/\D/g, "");
    setCorporateId(cleaned);
    setCorporateIdBlurredWithError(isInvalidKennitala(cleaned));
  };

  const handleCorporateKennitalaChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const cleaned = e.target.value.replace(/\D/g, "");
    setCorporateId(cleaned);
    if (cleaned.length === KENNITALA_LENGTH) {
      setCorporateIdBlurredWithError(false);
    }
  };

  const handleCorporateKennitalaFocus = () => {
    setCorporateIdBlurredWithError(false);
  };

  const handleBankChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, "");
    setBank(value);
    if (value.length === 4) {
      hbRef.current?.focus();
    }
  };

  const handleHbChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, "");
    setHb(value);
    if (value.length === 2) {
      reiknRef.current?.focus();
    }
  };

  const handleBankBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value.length > 0) {
      setBank(value.padStart(4, "0"));
    }
  };

  const handleReiknBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value.length > 0) {
      setReikn(value.padStart(6, "0"));
    }
  };

  const handleHbBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value.length > 0) {
      const paddedValue = value.padStart(2, "0");
      setHb(paddedValue);
      setHbBlurredWithWarning(paddedValue !== "26");
    } else {
      setHbBlurredWithWarning(false);
    }
  };

  const handleReiknKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (!isFormInvalid) {
        addAccount();
      }
    }
  };


  const numericInputProps = {
    inputMode: "numeric" as const,
    pattern: "[0-9]*",
    className: "h-12",
    autoComplete: "off" as const,
  };

  const content = (
    <div className="flex flex-col space-y-4 w-full text-left">
      {!inModal && (
        <span className="text-sm font-medium">
          Nýr bankareikningur (BBAN)
        </span>
      )}

      {isCorporate && (
        <div className="grid gap-2">
          <Label htmlFor="corporateId">Kennitala fyrirtækis</Label>
          <Input
            id="corporateId"
            placeholder="Kennitala fyrirtækis"
            maxLength={KENNITALA_LENGTH}
            value={corporateId}
            onChange={handleCorporateKennitalaChange}
            onBlur={handleCorporateKennitalaBlur}
            onFocus={handleCorporateKennitalaFocus}
            aria-invalid={!!corporateIdError}
            {...numericInputProps}
          />
          <div className="h-5 text-sm">
            {corporateIdError && (
              <span className="text-destructive">{corporateIdError}</span>
            )}
          </div>
        </div>
      )}

      <div className="mb-4">
        <Label htmlFor="bban-bank" className="block mb-2">
          Greiðslureikningur
        </Label>
        <div className="grid grid-cols-[minmax(3rem,2fr)_auto_minmax(4rem,3fr)] gap-1.5 items-stretch w-full max-w-full">
          <Input
            id="bban-bank"
            placeholder="Banki"
            maxLength={4}
            value={bank}
            onChange={handleBankChange}
            onBlur={handleBankBlur}
            {...numericInputProps}
            className="h-12 w-full min-w-0"
          />
          <Input
            ref={hbRef}
            id="bban-hb"
            placeholder="Hb"
            maxLength={2}
            value={hb}
            onChange={handleHbChange}
            onBlur={handleHbBlur}
            {...numericInputProps}
            className="h-12 w-12 shrink-0 min-w-0"
          />
          <Input
            ref={reiknRef}
            id="bban-reikn"
            placeholder="Rkn"
            maxLength={6}
            value={reikn}
            onChange={(e) => setReikn(e.target.value.replace(/\D/g, ""))}
            onBlur={handleReiknBlur}
            onKeyDown={handleReiknKeyDown}
            {...numericInputProps}
            className="h-12 w-full min-w-0"
          />
        </div>
      </div>

      <div className="h-5 text-sm">
        {isHbWarning && (
          <span className="text-amber-600">Athugið að reikningur þarf að vera óbundinn.</span>
        )}
      </div>

      <Button
        onClick={addAccount}
        disabled={isFormInvalid}
        className={
          isFormInvalid
            ? `${FULL_WIDTH_TALL_BUTTON} ${GREEN_ACTION_BUTTON_DISABLED}`
            : `${FULL_WIDTH_TALL_BUTTON} ${GREEN_ACTION_BUTTON_ENABLED}`
        }
      >
        <div className="flex items-center gap-1">
          <span>Bæta við reikning</span>
          <AddWalletIcon className="size-4 shrink-0" />
        </div>
      </Button>
    </div>
  );

  if (inModal) {
    return content;
  }

  return (
    <Card className="w-full gap-0">
      <CardContent className="px-4">{content}</CardContent>
    </Card>
  );
}
